import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Smartphone, Loader2 } from "lucide-react";

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nom, setNom] = useState("");
  const [loading, setLoading] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const loggedUser = await login(email, password);
        toast.success("Connexion réussie");
        if (loggedUser?.role === "technicien") {
          navigate("/atelier");
        } else if (loggedUser?.role === "manager") {
          navigate("/stock");
        } else {
          navigate("/dashboard");
        }
      } else {
        await register(email, password, nom);
        setRegisterSuccess(true);
        toast.success("Demande envoyée !");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_72eac30c-f09c-4f57-a754-51210f45a00e/artifacts/dljsg2et_4LKSzScXZCI-wq_cQXQSr.png')`,
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-bold font-heading mb-4">
            EMS Gestion
          </h2>
          <p className="text-lg text-slate-300 max-w-md">
            Solution ERP complète pour gérer vos achats, votre stock et vos ventes de téléphones d&apos;occasion.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <Card className="w-full max-w-md border-0 shadow-none">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-xl bg-primary flex items-center justify-center">
                <Smartphone className="h-7 w-7 text-primary-foreground" />
              </div>
            </div>
            <CardTitle className="text-2xl font-heading">
              {isLogin ? "Connexion" : "Demander un accès"}
            </CardTitle>
            <CardDescription>
              {isLogin
                ? "Entrez vos identifiants pour accéder à l'ERP"
                : "Votre demande sera validée par un administrateur"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {registerSuccess ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold">Demande envoyée !</h3>
                <p className="text-sm text-muted-foreground">
                  Votre demande de compte a été envoyée. Un administrateur doit valider votre compte et vous attribuer un rôle avant que vous puissiez vous connecter.
                </p>
                <Button
                  variant="outline"
                  onClick={() => { setRegisterSuccess(false); setIsLogin(true); }}
                  className="mt-2"
                >
                  Retour à la connexion
                </Button>
              </div>
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom complet</Label>
                  <Input
                    id="nom"
                    placeholder="Jean Dupont"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    required={!isLogin}
                    data-testid="register-nom-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemple.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLogin ? "Se connecter" : "Envoyer la demande"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin
                  ? "Pas encore de compte ? Demander un accès"
                  : "Déjà un compte ? Se connecter"}
              </button>
            </div>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
